import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Typography,
  Space,
  List,
  Tag,
  Empty,
  Spin,
  Row,
  Col,
  message,
} from 'antd';
import {
  HistoryOutlined,
  ArrowLeftOutlined,
  CarOutlined,
  FileTextOutlined,
  ToolOutlined,
  RightOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import PublicLayout from '../components/landing/PublicLayout';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { customerPortalApi } from '../services/apiInstances';

const { Title, Text } = Typography;

const statusColor = (status?: string): string => {
  switch ((status || '').toLowerCase()) {
    case 'approved':
    case 'completed':
    case 'invoiced':
      return 'green';
    case 'declined':
    case 'rejected':
    case 'cancelled':
      return 'red';
    case 'sent':
    case 'pending':
    case 'in_progress':
    case 'scheduled':
      return 'blue';
    default:
      return 'default';
  }
};

const money = (value: any): string => {
  const n = Number(value);
  return `$${(Number.isFinite(n) ? n : 0).toFixed(2)}`;
};

interface HistoryItem {
  id: number;
  type: 'estimate' | 'work_order';
  number: string;
  status: string;
  total_amount: number;
  created_at: string;
  year?: number;
  make?: string;
  model?: string;
}

const CustomerHistory: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useCustomerAuth();
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState<HistoryItem[]>([]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/customer/login');
      return;
    }
    const load = async () => {
      setLoading(true);
      try {
        const res = await customerPortalApi.get('/customer-portal/service-history');
        setHistory(res.data.serviceHistory || []);
      } catch (error) {
        message.error('Could not load your service history.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isAuthenticated, navigate]);

  const openItem = (item: HistoryItem) => {
    if (item.type === 'work_order') {
      navigate(`/customer/work-orders/${item.id}`);
    } else {
      navigate(`/customer/estimates/${item.id}`);
    }
  };

  if (loading) {
    return (
      <PublicLayout>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <Spin size="large" />
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f0f2f5 0%, #ffffff 100%)',
        padding: '40px 20px'
      }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          {/* Header */}
          <Card style={{ marginBottom: 24 }}>
            <Space direction="vertical" size={0}>
              <Button
                type="link"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/customer/dashboard')}
                style={{ padding: 0, marginBottom: 8 }}
              >
                Back to Dashboard
              </Button>
              <Title level={3} style={{ margin: 0 }}>
                <HistoryOutlined /> Service History
              </Title>
            </Space>
          </Card>

          {/* List */}
          <Card>
            {history.length > 0 ? (
              <List
                dataSource={history}
                renderItem={(item) => (
                  <List.Item
                    style={{ cursor: 'pointer' }}
                    onClick={() => openItem(item)}
                  >
                    <List.Item.Meta
                      avatar={
                        item.type === 'estimate' ? (
                          <FileTextOutlined style={{ fontSize: 24, color: '#1890ff' }} />
                        ) : (
                          <ToolOutlined style={{ fontSize: 24, color: '#722ed1' }} />
                        )
                      }
                      title={
                        <Space wrap>
                          <Text strong>{item.number}</Text>
                          <Tag color={statusColor(item.status)}>
                            {(item.status || 'unknown').toUpperCase()}
                          </Tag>
                          <Tag>{item.type === 'estimate' ? 'Estimate' : 'Work Order'}</Tag>
                        </Space>
                      }
                      description={
                        <Space direction="vertical" size={0}>
                          <Text type="secondary">
                            <CarOutlined /> {item.year} {item.make} {item.model}
                          </Text>
                          <Text type="secondary">
                            {dayjs(item.created_at).format('MMM D, YYYY')}
                          </Text>
                        </Space>
                      }
                    />
                    <Row align="middle" gutter={12}>
                      <Col>
                        <Text strong style={{ fontSize: 16 }}>{money(item.total_amount)}</Text>
                      </Col>
                      <Col>
                        <RightOutlined style={{ color: '#bbb' }} />
                      </Col>
                    </Row>
                  </List.Item>
                )}
              />
            ) : (
              <Empty
                description="No service history yet"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ padding: '60px 20px' }}
              >
                <Button type="primary" onClick={() => navigate('/book')}>
                  Book a Service
                </Button>
              </Empty>
            )}
          </Card>
        </div>
      </div>
    </PublicLayout>
  );
};

export default CustomerHistory;
